import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faUsers, 
  faUserTie, 
  faUserGraduate, 
  faBoxOpen, 
  faHourglassHalf, 
  faWallet, 
  faSyncAlt,
  faChartLine,
  faArrowUp,
  faShieldAlt,
  faCrown
} from "@fortawesome/free-solid-svg-icons";

import { getStatsFromIDB, setStatsToIDB } from '../../../utils/idbStats';
import { getStatService } from "../../../services/playList-service";
import "./stats.css";

const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon: any;
  color: string;
  delay?: number;
  subtitle?: string;
}> = ({ title, value, icon, color, delay = 0, subtitle }) => {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
      className="bg-white/80 backdrop-blur-xl p-5 lg:p-7 rounded-[2.5rem] border border-white shadow-[0_20px_50px_rgba(0,0,0,0.03)] flex flex-col justify-between group h-full transition-all hover:shadow-[0_30px_60px_rgba(0,0,0,0.08)]"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-2xl ${color} flex items-center justify-center text-xl lg:text-2xl shadow-lg transition-transform group-hover:scale-110 duration-500`}>
          <FontAwesomeIcon icon={icon} />
        </div>
        <div className="flex flex-col items-end">
        </div>
      </div>
      
      <div>
        <p className="text-[10px] lg:text-[11px] font-black text-text/30 uppercase tracking-[0.2em] mb-1">{title}</p>
        <h3 className="text-2xl lg:text-3xl font-black text-title tracking-tighter leading-none">{value}</h3>
        {subtitle && (
          <p className="text-[9px] font-bold text-text/20 uppercase tracking-widest mt-3 leading-tight">{subtitle}</p>
        )}
      </div>
    </motion.div>
  );
};

const Stats: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const CACHE_MAX_AGE = 10 * 60 * 1000; // 10 minutes

  const fetchStats = React.useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      const cached = await getStatsFromIDB('stats');
      if (!forceRefresh && cached && cached.stats) {
        setData(cached.stats);
      }
      const now = Date.now();
      if (forceRefresh || !cached || !cached.timestamp || now - cached.timestamp > CACHE_MAX_AGE) {
        try {
          const res = await getStatService();
          setData(res.data);
          setStatsToIDB('stats', res.data, now);
        } catch (e) {
          console.error("API error", e);
        }
      }
    } finally {
      setTimeout(() => setLoading(false), 600);
    }
  }, [CACHE_MAX_AGE]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFB] p-3 md:p-6 lg:p-10">
      <div className="max-w-[1500px] mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 lg:mb-14">
          <div>
            <div className="flex items-center gap-2 mb-2">
               <div className="w-6 h-1 bg-primary rounded-full"></div>
               <span className="text-[9px] font-black text-primary uppercase tracking-[0.4em]">Analytics</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-black text-title tracking-tighter uppercase italic leading-none">
              Performance <span className="text-primary italic">Global</span>
            </h1>
          </div>
          
          <button
            onClick={() => fetchStats(true)}
            disabled={loading}
            className="group relative flex items-center gap-4 px-7 py-4 bg-[#05322B] text-white rounded-2xl lg:rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest shadow-xl hover:shadow-emerald-500/20 transition-all active:scale-95 disabled:opacity-70"
          >
            <FontAwesomeIcon icon={faSyncAlt} className={`${loading ? 'animate-spin' : ''} text-emerald-400`} />
            <span>{loading ? 'Update...' : 'Actualiser'}</span>
          </button>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 2xl:grid-cols-12 gap-6 lg:gap-10"
        >
          
          {/* Hero Card: Revenue */}
          <motion.div 
            variants={{ hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1 } }}
            className="2xl:col-span-8 bg-white p-8 lg:p-12 rounded-[3rem] lg:rounded-[4rem] shadow-[0_32px_128px_rgba(0,0,0,0.04)] border border-white/50 relative overflow-hidden group"
          >
             <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-emerald-50/40 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
             
             <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                <div className="flex-1 space-y-6">
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-title text-white flex items-center justify-center text-lg shadow-lg">
                         <FontAwesomeIcon icon={faChartLine} />
                      </div>
                      <h2 className="text-[11px] font-black text-title/40 uppercase tracking-[0.3em]">Volume Transactions</h2>
                   </div>
                   <div className="space-y-4">
                      <p className="text-5xl lg:text-7xl font-black text-title tracking-tighter leading-none italic">
                        {data?.totalPrice ? Math.floor(data.totalPrice).toLocaleString() : '0'}<span className="text-primary ml-3">TND</span>
                      </p>
                      <div className="flex flex-wrap items-center gap-4">
                         <span className="text-[9px] font-black text-text/20 uppercase tracking-widest italic pt-2">Paiements validés cumulés</span>
                      </div>
                   </div>
                </div>

                <div className="flex flex-row lg:flex-col gap-4 min-w-fit">
                   <div className="flex-1 lg:w-44 bg-slate-50 p-5 rounded-[2rem] border border-white shadow-sm flex flex-col justify-center">
                      <p className="text-[9px] font-black text-text/30 uppercase tracking-[0.2em] mb-1">Commandes</p>
                      <p className="text-3xl font-black text-title tracking-tighter italic">{data?.totalOrders || 0}</p>
                   </div>
                   <div className="flex-1 lg:w-44 bg-slate-50 p-5 rounded-[2rem] border border-white shadow-sm flex flex-col justify-center">
                      <p className="text-[9px] font-black text-text/30 uppercase tracking-[0.2em] mb-1">En attente</p>
                      <p className="text-3xl font-black text-rose-500 tracking-tighter italic">{data?.totalPendingOrders || 0}</p>
                   </div>
                </div>
             </div>
          </motion.div>

          {/* User Total Card */}
          <motion.div 
            variants={{ hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1 } }}
            className="2xl:col-span-4 bg-[#05322B] p-8 lg:p-12 rounded-[3.5rem] lg:rounded-[4rem] relative overflow-hidden group shadow-2xl shadow-emerald-900/10"
          >
             <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent"></div>
             
             <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                   <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md text-emerald-400 flex items-center justify-center text-xl mb-6 border border-white/10">
                      <FontAwesomeIcon icon={faUsers} />
                   </div>
                   <h2 className="text-[10px] font-black text-emerald-400 uppercase tracking-[0.4em] mb-2">Communauté</h2>
                   <p className="text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none italic">
                     {data?.totalUsers || 0}
                   </p>
                </div>
                <div className="mt-8 pt-8 border-t border-white/5">
                   <span className="text-[9px] font-bold text-white/30 uppercase tracking-[0.2em]">Enregistrements Plateforme</span>
                </div>
             </div>
          </motion.div>

          {/* Lower Grid: Professors */}
          <motion.div className="2xl:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
             <div className="sm:col-span-2">
                <h3 className="text-[10px] font-black text-title/40 uppercase tracking-[0.4em] flex items-center gap-3">
                   <div className="w-1.5 h-4 bg-primary rounded-full"></div>
                   Segment Enseignants
                </h3>
             </div>
             <StatCard 
               title="Effectif Total" 
               value={data?.totalTeachers || 0} 
               icon={faUserTie} 
               color="bg-purple-50 text-purple-600"
               delay={0.1}
             />
             <StatCard 
               title="Enseignants Super" 
               value={data?.totalSuperTeachers || 0} 
               icon={faCrown} 
               color="bg-amber-50 text-amber-500" 
               delay={0.2}
             />
          </motion.div>

          {/* Lower Grid: Students */}
          <motion.div className="2xl:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
             <div className="sm:col-span-2">
                <h3 className="text-[10px] font-black text-title/40 uppercase tracking-[0.4em] flex items-center gap-3">
                   <div className="w-1.5 h-4 bg-primary rounded-full"></div>
                   Segment Élèves
                </h3>
             </div>
             <StatCard 
               title="Effectif Élèves" 
               value={data?.totalStudents || 0} 
               icon={faUserGraduate} 
               color="bg-blue-50 text-blue-600" 
               delay={0.3}
             />
             <StatCard 
               title="Offres Validées" 
               value={data?.totalStudentsWithEnrolledOffer || 0} 
               icon={faShieldAlt} 
               color="bg-emerald-50 text-emerald-600" 
               delay={0.4}
             />
          </motion.div>

        </motion.div>

        <div className="mt-14 lg:mt-20 text-center opacity-20">
            <p className="text-[9px] font-black uppercase tracking-[0.6em]">System Analytics v2.0 • FOK NJE7EK</p>
        </div>
      </div>
    </div>
  );
};

export default Stats;
